import React, { useState } from 'react';
import { useField } from 'formik';
import { IFormikFieldProps } from './FormikFieldProps';
import { FormControl, FormControlProps, InputGroup } from 'react-bootstrap';
import MediaUpload from '../mediaUpload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export type InputTextProps = IFormikFieldProps &
  React.InputHTMLAttributes<HTMLInputElement> &
  FormControlProps & {
    isValidIndicator?: boolean;
  };

export type InputNumberProps = InputTextProps & { isInteger?: boolean };

export const InputText = React.forwardRef<HTMLInputElement, InputTextProps>(
  (props, ref) => {
    const [field, { value, touched, error }] = useField(props);
    const { isValidIndicator, ...rest } = props;

    return (
      <FormControl
        ref={ref as React.RefObject<HTMLInputElement & typeof FormControl>} // https://github.com/react-bootstrap/react-bootstrap/issues/3568
        isValid={isValidIndicator && value && touched && !error}
        isInvalid={touched && !!error}
        {...field}
        {...rest}
      />
    );
  },
);

export const InputColor: React.FC<InputTextProps> = (props) => {
  return <InputText style={{ width: '100px' }} type="color" {...props} />;
};

export const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
  (props, ref) => {
    const { isInteger, ...restProps } = props;
    const [, , { setValue }] = useField(props);

    return (
      <InputText
        type="number"
        {...restProps}
        ref={ref}
        onBlur={(e) => {
          const { min, max, value } = e.currentTarget;

          const valueAsString = value.replace('e', '');
          let valueAsNumber = parseFloat(valueAsString);

          if (valueAsString === '') {
            setValue(min ? min.toString() : '0');
            return;
          }

          if (isInteger && valueAsNumber.toString().indexOf('.') !== -1) {
            valueAsNumber = Math.round(valueAsNumber);
          }

          if (min && valueAsNumber < parseFloat(min)) {
            valueAsNumber = parseFloat(min);
          }

          if (max && valueAsNumber > parseFloat(max)) {
            valueAsNumber = parseFloat(max);
          }

          if (value !== valueAsNumber.toString()) {
            setValue(valueAsNumber.toString());
          }
        }}
      />
    );
  },
);

export const InputTime: React.FC<InputTextProps> = (props) => {
  return <InputText type="time" {...props} />;
};

export const InputRange: React.FC<
  InputTextProps & { showValue?: boolean; valueSuffix?: string }
> = (props) => {
  const [, { value }] = useField(props);
  const { showValue, valueSuffix, ...rest } = props;

  return (
    <>
      <InputText type="range" {...rest} />
      {showValue && (
        <div className="text-center">
          <small>{`${value}${valueSuffix}`}</small>
        </div>
      )}
    </>
  );
};

export const InputPassword: React.FC<InputTextProps> = (props) => {
  const [, { error }] = useField(props);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  return (
    <InputGroup className={`${error ? 'is-invalid' : ''}`}>
      <InputText
        autoComplete="new-password"
        type={`${showPassword ? 'text' : 'password'}`}
        {...props}
      />
      <InputGroup.Append onClick={() => setShowPassword(!showPassword)}>
        <InputGroup.Text>
          <FontAwesomeIcon
            icon={`${showPassword ? 'eye' : 'eye-slash'}`}
            transform={{ rotate: 180 }}
          />
        </InputGroup.Text>
      </InputGroup.Append>
    </InputGroup>
  );
};

export const InputTelephone: React.FC<InputTextProps> = (props) => {
  return <InputText autoComplete="new-password" type="tel" {...props} />;
};

export const InputEmail: React.FC<InputTextProps> = (props) => {
  return <InputText autoComplete="new-password" type="email" {...props} />;
};

export const InputUpperCase: React.FC<InputTextProps> = (props) => {
  const [, , { setValue }] = useField(props);

  return (
    <InputText
      {...props}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        setValue(e.target.value.toUpperCase())
      }
    />
  );
};

export const InputFile: React.FC<InputTextProps & { url: string }> = (
  props,
) => {
  const [field, , { setValue, setTouched }] = useField(props);

  return (
    <MediaUpload
      id={field.name}
      {...props}
      onChange={(field: string, mediaId: string) => {
        setValue(mediaId);
        setTouched(true);
      }}
    />
  );
};
